import {
  Box,
  Checkbox,
  CircularProgress,
  Grid,
  Link,
  makeStyles,
} from '@material-ui/core';
import { get, isEmpty } from 'lodash';
import { OrangeButton } from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import InputField from 'components/InputField';
import PasswordInputField from 'components/InputField/Password';
import Modal from 'components/Modal';
import messages from 'containers/LoginPage/messages';
import {
  formStyle,
  SizedLongBody,
  StyledBodyText,
  StyledCaption,
  StyledFormControlLabel,
} from 'containers/LoginPage/styles';
import validationSchema from 'containers/LoginPage/validationSchema';
import { FORGOT_PASSWORD_LOGIN } from 'containers/PasswordRecoveryPages/constants';
import { FormikProps, withFormik } from 'formik';
import GATracking, { EVENT_ACTION, EVENT_CATEGORIES } from 'helpers/GATracking';
import GaClickHOC from 'helpers/GATracking/GaClickHOC';
import GaLoadHOC from 'helpers/GATracking/GaLoadHOC';
import { isReverse, translate } from 'helpers/intl';
import { pushTo } from 'helpers/routes';
import React, { useState } from 'react';
import { Info, Lock, User } from 'react-feather';
import { injectIntl, IntlShape } from 'react-intl';
import { useSelector } from 'react-redux';
import PAGES from 'routes/pages';
import { getLocaleSelector } from 'store/LanguageProvider/selectors';

const useStyles = makeStyles({
  ...formStyle,
});

const InnerForm = (
  props: {
    loader?: boolean;
    setPasswordRecoveryStep: Function;
    responseErrorMessage?: string;
  } & FormikProps<FormValues>,
) => {
  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    loader,
    touched,
  } = props;

  const classes = useStyles();

  const handleIdchange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(event);
    if (values.isSaveUserId)
      localStorage.setItem('identifiant', event.target.value);
  };
  const [open, setOpen] = useState(false);

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleOnClickSaveId = () => {
    GATracking.event(
      EVENT_CATEGORIES.LOGIN,
      EVENT_ACTION.CLICK,
      'Mémoriser mon identifiant',
    );
    !values.isSaveUserId
      ? localStorage.setItem('identifiant', values.identifiant)
      : localStorage.setItem('identifiant', '');
  };

  const lang = useSelector(getLocaleSelector);
  const reverse = isReverse(lang);

  return (
    <form onSubmit={handleSubmit}>
      {!isEmpty(get(props, 'responseErrorMessage')) && (
        <GaLoadHOC
          category={EVENT_CATEGORIES.LOGIN}
          label="Chargement du bloc d'erreur"
          action={EVENT_ACTION.ERROR}
        >
          <Box mb={2}>
            <Grid item>
              <ErrorMessage
                message={translate({ ...messages.loginError }, props)}
              />
            </Grid>
          </Box>
        </GaLoadHOC>
      )}
      <Box mb={3}>
        <Grid item>
          <InputField
            name="identifiant"
            placeholder={translate({ ...messages.identifiant }, props)}
            value={values.identifiant}
            startAdornment={<User />}
            endAdornment={
              <Info
                className={classes.infoIcon}
                onClick={() => handleOpenModal()}
              />
            }
            error={touched.identifiant && errors.identifiant}
            helperText={touched.identifiant && errors.identifiant}
            handleChange={handleIdchange}
            handleBlur={handleBlur}
          />
        </Grid>
      </Box>

      <Box mb={1.25}>
        <Grid item>
          <PasswordInputField
            name="password"
            placeholder={translate({ ...messages.password }, props)}
            value={values.password}
            error={touched.password && errors.password}
            helperText={touched.password && errors.password}
            startAdornment={<Lock />}
            handleChange={handleChange}
            handleBlur={handleBlur}
            gAEventCategory={EVENT_CATEGORIES.LOGIN}
          />
        </Grid>
      </Box>
      <StyledCaption>
        <Link
          data-testid="identifiant-oublie"
          classes={{ root: classes.forgetPasswordStyle }}
          href=""
          onClick={(event: React.SyntheticEvent) => {
            event.preventDefault();
            GATracking.event(
              EVENT_CATEGORIES.LOGIN,
              EVENT_ACTION.CLICK,
              'Identifiant oublié ou MPD oublié',
            );
            props.setPasswordRecoveryStep(FORGOT_PASSWORD_LOGIN);
            pushTo(PAGES.RECOVERY_PASSWORD);
          }}
        >
          {translate({ ...messages.forgotPass }, props)}
        </Link>
      </StyledCaption>
      <Grid
        container
        direction="row"
        alignItems="flex-end"
        classes={{ root: classes.gridMarginBottom }}
      >
        <StyledFormControlLabel
          control={
            <Checkbox
              color="primary"
              name="isSaveUserId"
              value={values.isSaveUserId}
              onChange={handleChange}
              onClick={handleOnClickSaveId}
              checked={values.isSaveUserId}
              data-testid="save-user"
            />
          }
          label={translate({ ...messages.rememberMe }, props)}
          reverse={reverse}
        />
      </Grid>
      <Grid item>
        <Box mb={2}>
          <GaClickHOC category={EVENT_CATEGORIES.LOGIN} label="se connecter">
            <OrangeButton type="submit" variant="contained">
              {loader ? (
                <CircularProgress data-testid="loader" size={20} />
              ) : (
                <React.Fragment>
                  {translate({ ...messages.seConnecter }, props)}
                </React.Fragment>
              )}
            </OrangeButton>
          </GaClickHOC>
        </Box>
      </Grid>
      <Modal
        open={open}
        handleOpen={handleOpenModal}
        handleClose={handleCloseModal}
        header={
          <SizedLongBody weight="500" color="#04336B">
            {translate(messages.modalHeader, props)}
          </SizedLongBody>
        }
        content={
          <StyledBodyText>
            {translate(messages.modalContent, props)}
          </StyledBodyText>
        }
        footer={
          <Grid container justify="flex-end">
            <Grid item>
              {' '}
              <OrangeButton variant="contained" onClick={handleCloseModal}>
                {translate(messages.close, props)}
              </OrangeButton>
            </Grid>
          </Grid>
        }
      ></Modal>
    </form>
  );
};

export interface FormProps {
  requestLogin: Function;
  intl: IntlShape;
  loader?: boolean;
  setPasswordRecoveryStep: Function;
  responseErrorMessage?: string;
}

interface FormValues {
  password: string;
  identifiant: string;
  isSaveUserId: boolean;
}

const Form = withFormik<FormProps, FormValues>({
  mapPropsToValues: () => {
    return {
      identifiant: localStorage.getItem('identifiant') || '',
      password: '',
      isSaveUserId: localStorage.getItem('identifiant') ? true : false,
    };
  },

  validationSchema,

  handleSubmit: (values, { props }) => {
    return props.requestLogin(values);
  },
})(InnerForm);

export default injectIntl(Form);
